'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { fleetApiGo } from '../../../../api/axiosClient';
import FormDialog from '../../../../components/FormDialog';
import ViewAllServiceReminder from './ViewAllServiceReminder';
import { useLanguage } from '../../../../utils/i18n';

function ServiceReminderContent() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const resp = await fleetApiGo.get('/dashboard-work-order/total');
      const rawData = resp?.data || [];
      const grouped = rawData.reduce((acc, item) => {
        const key = item.service_type_name;
        if (!acc[key]) {
          acc[key] = {
            service_type_name: key,
            danger: 0,
            warning: 0,
            total: 0
          };
        }
        if (item.notif_name === 'Danger') {
          acc[key].danger += item.total;
        }
        if (item.notif_name === 'Warning' || item.notif_name_dist_both === 'Warning') {
          acc[key].warning += item.total;
        }
        acc[key].total += item.total;

        return acc;
      }, {});
      setData(Object.values(grouped));
    } catch (err) {
      console.error('Fetch summary error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleOpenDetail = (serviceName, status = 'all') => {
    setSelectedService(serviceName);
    setSelectedStatus(status);
    setOpenDialog(true);
  };

  const handleCloseDetail = () => {
    setOpenDialog(false);
    setSelectedService('');
    setSelectedStatus('');
  };

  // Helper function - taruh di luar komponen atau di atas return
  const truncateText = (text, maxLength = 24) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;
  };

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );

  if (!loading && data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
          borderRadius: 3,
          border: '1px dashed #cbd5e1',
          minHeight: 300
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <NotificationsOffIcon sx={{ fontSize: 40, color: '#64748b' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
          {t('serviceReminder.noServiceReminders', 'No Service Reminders')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 400 }}>
          {t(
            'serviceReminder.noServiceRemindersDesc',
            "You're all caught up! There are currently no vehicles requiring maintenance or service reminders at this time."
          )}
        </Typography>
      </Paper>
    );
  }

  const filteredData = data.filter((item) => item.service_type_name?.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <Box>
      <Box mb={1} display="flex" justifyContent={{ xs: 'center', sm: 'space-between' }} alignItems="center" flexWrap="wrap" gap={2}>
        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small" aria-label="view mode">
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          placeholder={t('serviceReminder.searchByServiceType', 'Search by Service Type...')}
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'white', borderRadius: 1 }}
        />
      </Box>
      {viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {filteredData.map((item, index) => {
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0px 10px 20px rgba(0,0,0,0.05)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', color: '#3b82f6', display: 'flex' }}>
                        <BuildIcon fontSize="medium" />
                      </Box>
                      <Box>
                        <Tooltip title={item.service_type_name} placement="top">
                          <Typography variant="h6" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.2 }}>
                            {truncateText(item.service_type_name, 20)}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {t('serviceReminder.preventiveMaintenance', 'Preventive Maintenance')}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={`${item.total} ${t('serviceReminder.total', 'Total')}`}
                      size="small"
                      sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, borderRadius: 1.5 }}
                    />
                  </Box>

                  <Stack direction="row" spacing={1.5} mb={3}>
                    <Box
                      onClick={() => handleOpenDetail(item.service_type_name, 'Danger')}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#fef2f2',
                        border: '1px solid #fee2e2',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                        ...(item.danger > 0 && {
                          animation: 'pulseDanger 1.5s infinite ease-in-out',
                          '@keyframes pulseDanger': {
                            '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.6)' },
                            '50%': { transform: 'scale(1.03)', boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.3)' }
                          }
                        })
                      }}
                    >
                      <Typography variant="caption" color="error.main" fontWeight="600" display="block" sx={{ letterSpacing: 0.5 }}>
                        {t('serviceReminder.critical', 'CRITICAL')}
                      </Typography>
                      <Box display="flex" alignItems="baseline" gap={0.5}>
                        <Typography variant="h4" color="error.main" fontWeight="800">
                          {item.danger}
                        </Typography>
                        <Typography variant="caption" color="error.main" sx={{ fontWeight: 500 }}>
                          {t('serviceReminder.vehicles', 'vehicle(s)')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      onClick={() => handleOpenDetail(item.service_type_name, 'Warning')}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#fffbeb',
                        border: '1px solid #fef3c7',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#fef3c7', borderColor: '#fcd34d' },
                        ...(item.warning > 0 && {
                          animation: 'pulseWarning 1.5s infinite ease-in-out',
                          '@keyframes pulseWarning': {
                            '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.6)' },
                            '50%': { transform: 'scale(1.03)', boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.3)' }
                          }
                        })
                      }}
                    >
                      <Typography variant="caption" color="warning.main" fontWeight="600" display="block" sx={{ letterSpacing: 0.5 }}>
                        {t('serviceReminder.warning', 'WARNING')}
                      </Typography>
                      <Box display="flex" alignItems="baseline" gap={0.5}>
                        <Typography variant="h4" color="warning.main" fontWeight="800">
                          {item.warning}
                        </Typography>
                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                          {t('serviceReminder.vehicles', 'vehicle(s)')}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>

                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleOpenDetail(item.service_type_name, 'all')}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#475569',
                      borderColor: '#e2e8f0',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                    }}
                  >
                    {t('serviceReminder.viewServiceDetails', 'View Service Details')}
                  </Button>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Table sx={{ minWidth: 650 }} aria-label="service reminder table">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>{t('serviceReminder.serviceType', 'Service Type')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>
                  {t('serviceReminder.critical', 'CRITICAL')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>
                  {t('serviceReminder.warning', 'WARNING')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>
                  {t('serviceReminder.total', 'Total')}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>
                  {t('general.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: '#3b82f6', display: 'flex' }}>
                        <BuildIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight="600" color="text.primary">
                          {item.service_type_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('serviceReminder.preventiveMaintenance', 'Preventive Maintenance')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${item.danger} ${t('serviceReminder.vehicles', 'vehicle(s)')}`}
                      size="small"
                      onClick={() => handleOpenDetail(item.service_type_name, 'Danger')}
                      sx={{
                        bgcolor: '#fef2f2',
                        color: 'error.main',
                        border: '1px solid #fee2e2',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                        ...(item.danger > 0 && {
                          animation: 'pulseDangerChip 1.5s infinite ease-in-out',
                          '@keyframes pulseDangerChip': {
                            '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.6)' },
                            '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.3)' }
                          }
                        })
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${item.warning} ${t('serviceReminder.vehicles', 'vehicle(s)')}`}
                      size="small"
                      onClick={() => handleOpenDetail(item.service_type_name, 'Warning')}
                      sx={{
                        bgcolor: '#fffbeb',
                        color: 'warning.main',
                        border: '1px solid #fef3c7',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fef3c7', borderColor: '#fcd34d' },
                        ...(item.warning > 0 && {
                          animation: 'pulseWarningChip 1.5s infinite ease-in-out',
                          '@keyframes pulseWarningChip': {
                            '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.6)' },
                            '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.3)' }
                          }
                        })
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${item.total} ${t('serviceReminder.total', 'Total')}`}
                      size="small"
                      sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenDetail(item.service_type_name, 'all')}
                      sx={{ textTransform: 'none', borderRadius: 2, color: '#475569', borderColor: '#e2e8f0' }}
                    >
                      {t('serviceReminder.viewDetails', 'View Details')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t('serviceReminder.noServiceTypesFound', 'No service types found.')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FormDialog
        open={openDialog}
        onClose={handleCloseDetail}
        title={`${t('serviceReminder.title', 'Service Reminder')}: ${selectedService} (${selectedStatus === 'all' ? t('serviceReminder.all', 'All') : selectedStatus})`}
        fullScreenMode={false}
        maxWidth="lg"
        actions={
          <Button variant="contained" onClick={handleCloseDetail} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {t('general.close', 'Close')}
          </Button>
        }
      >
        <ViewAllServiceReminder serviceTypeName={selectedService} statusFilter={selectedStatus} />
      </FormDialog>
    </Box>
  );
}

export default function ServiceReminder() {
  const { t } = useLanguage();
  return (
    <Box sx={{ bgcolor: '#f9fafb', minHeight: '100vh' }}>
      <Box mb={1}>
        <Typography variant="h4" fontWeight="800" color="primary.main" gutterBottom>
          {t('serviceReminder.title', 'Service Reminder')}
        </Typography>
      </Box>
      <ServiceReminderContent />
    </Box>
  );
}
